import axios from "axios";
import { API_BASE_URL } from "../../config/apiConfig";

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

apiClient.interceptors.request.use((config) => {
  const user = JSON.parse(localStorage.getItem("user"));
  if (user?.token) {
    config.headers.Authorization = `Bearer ${user.token}`;
  }
  return config;
});

export const postApi = {
  /**
   * CREATE POST
   * Endpoint: POST /api/posts
   * Body: { postType, content, photos, question, researchPaper, visibility, tags }
   */
  createPost: async (postData) => {
    return apiClient.post("/posts", postData);
  },

  /**
   * UPDATE POST
   * Endpoint: PUT /api/posts/:id
   */
  updatePost: async (postId, updates) => {
    return apiClient.put(`/posts/${postId}`, updates);
  },

  /**
   * DELETE POST
   * Endpoint: DELETE /api/posts/:id
   */
  deletePost: async (postId, permanent = false) => {
    return apiClient.delete(`/posts/${postId}`, {
      params: { permanent },
    });
  },

  /**
   * LIKE/UNLIKE POST
   * Endpoint: POST /api/posts/:id/like
   * Response: { success, liked, likeCount }
   */
  toggleLike: async (postId) => {
    return apiClient.post(`/posts/${postId}/like`);
  },

  /**
   * ADD COMMENT
   * Endpoint: POST /api/posts/:id/comments
   * Body: { content }
   */
  addComment: async (postId, content) => {
    return apiClient.post(`/posts/${postId}/comments`, { content });
  },

  /**
   * DELETE COMMENT
   * Endpoint: DELETE /api/posts/:postId/comments/:commentId
   */
  deleteComment: async (postId, commentId) => {
    return apiClient.delete(`/posts/${postId}/comments/${commentId}`);
  },

  /**
   * LIKE COMMENT
   * Endpoint: POST /api/posts/:postId/comments/:commentId/like
   */
  toggleCommentLike: async (postId, commentId) => {
    return apiClient.post(`/posts/${postId}/comments/${commentId}/like`);
  },

  /**
   * ADD REPLY TO COMMENT
   * Endpoint: POST /api/posts/:postId/comments/:commentId/replies
   * Body: { content }
   */
  addReply: async (postId, commentId, content) => {
    return apiClient.post(`/posts/${postId}/comments/${commentId}/replies`, {
      content,
    });
  },

  /**
   * REPOST
   * Endpoint: POST /api/posts/:id/repost
   * Body: { content } (optional caption)
   */
  repost: async (postId, content = "") => {
    return apiClient.post(`/posts/${postId}/repost`, { content });
  },

  /**
   * SAVE/BOOKMARK POST
   * Endpoint: POST /api/saved/:postId
   * Body: { collection, tags, notes, priority } (all optional)
   * Response: { savedPost }
   */
  savePost: async (postId, saveData = {}) => {
    return apiClient.post(`/saved/${postId}`, saveData);
  },

  /**
   * UNSAVE POST
   * Endpoint: DELETE /api/saved/:postId
   * Response: { success, message }
   */
  unsavePost: async (postId) => {
    return apiClient.delete(`/saved/${postId}`);
  },

  /**
   * GET SAVED POSTS
   * Endpoint: GET /api/saved
   * Params: { collection, tags, priority, page, limit }
   * Response: { success, data: { savedPosts, pagination } }
   */
  getSavedPosts: async (params = {}) => {
    return apiClient.get("/saved", { params });
  },


  /**
   * TOGGLE SAVE (Legacy - uses new save/unsave internally)
   * @deprecated Use savePost/unsavePost instead
   */
  toggleSave: async (postId) => {
    return apiClient.post(`/posts/${postId}/save`);
  },

  /**
   * GET COMMENTS FOR A POST
   * Endpoint: GET /api/posts/:id/comments
   * This endpoint fetches all comments for a specific post
   */
  getComments: async (postId) => {
    return apiClient.get(`/posts/${postId}/comments`);
  },

  /**
   * GET SINGLE POST BY ID
   * Endpoint: GET /api/posts/:id
   * Returns: { success, post }
   */
  getPost: async (postId) => {
    return apiClient.get(`/posts/${postId}`);
  },

  /**
   * GET RELATED POSTS
   * Endpoint: GET /api/posts/:id/related
   * Returns: { success, posts } - Posts with matching tags/keywords
   */
  getRelatedPosts: async (postId, limit = 5) => {
    return apiClient.get(`/posts/${postId}/related`, {
      params: { limit },
    });
  },
};

export default postApi;
